import type { AppStateController } from '../state/AppStateController';
import type { PlatformUser } from '../platform/Platform';
import { createFuturePromise } from '../../utils/async-utils';
import fakeUsers from './fakeUsers.json';
import RandomDataGenerator from '../../libs/RandomDataGenerator';
import { MS_IN_HOUR } from '../../utils/time-utils';

export type Leaderboard = {
  pool: number;
  entries: LeaderboardEntry[];
};

export type LeaderboardEntry = {
  id: string;
  name: string;
  avatarUrl: string | null;
  level: number;
  currentUser: boolean;
};

export type LeaderboardServiceConfig = {
  currentUser: PlatformUser;
  realUsers: PlatformUser[];
};

const leaderboardConfig = {
  poolSize: 5,
  levelDiff: { min: 1, max: 3 },
  levelTimeIncrease: {
    increaseIntervalInHours: { min: 4, max: 10 },
    maxIncrease: 6,
  },
};

export class LeaderboardPoolService {
  private readonly initializedFP = createFuturePromise();

  private fakeUsers: PlatformUser[] | null = null;

  private realUsers: PlatformUser[] | null = null;

  private currentUser: PlatformUser | null = null;

  private readonly rnd: RandomDataGenerator = new RandomDataGenerator([]);

  constructor(private readonly state: AppStateController) {}

  public init(config: LeaderboardServiceConfig): void {
    this.rnd.sow([config.currentUser.id]);
    this.fakeUsers = this.rnd.shuffle(
      (fakeUsers as { name: string; avatarUrl: string }[]).map((entry, index) => ({
        id: `fake_${index}`,
        currentUser: false,
        ...entry,
      })),
    );
    this.realUsers = this.rnd.shuffle(config.realUsers.slice());

    this.currentUser = config.currentUser;
    this.initializedFP.resolve();
  }

  public isReady(): Promise<void> {
    return this.initializedFP.promise;
  }

  public getLeaderboard(playerScore: number): Leaderboard {
    if (!this.currentUser) {
      throw new Error('LeaderboardPoolService - not initialized');
    }

    const { pool, startLevel, joinedAt } = this.state.getLeaderboardPool();

    this.rnd.sow([this.currentUser.id]);

    const entries = this.insertPlayer(
      this.increaseOpponentsLevel(
        this.opponentsToLeaderboardEntries(this.getOpponentsForThePool(pool), startLevel),
        joinedAt,
      ),
      playerScore,
    );

    if (entries[entries.length - 1].currentUser) {
      this.state.setNewLeaderboardPool(pool + 1, playerScore);
    }

    return {
      pool,
      entries,
    };
  }

  private insertPlayer(leaderboard: LeaderboardEntry[], level: number): LeaderboardEntry[] {
    if (!this.currentUser) {
      throw new Error('LeaderboardPoolService.insertPlayer - not initialized');
    }

    leaderboard.splice(leaderboard.findLastIndex((entry) => entry.level <= level) + 1, 0, {
      ...this.currentUser,
      level,
    });

    return leaderboard;
  }

  private increaseOpponentsLevel(leaderboard: LeaderboardEntry[], joinedAt: number): LeaderboardEntry[] {
    const {
      increaseIntervalInHours: { min, max },
      maxIncrease,
    } = leaderboardConfig.levelTimeIncrease;
    const timeDiffInHours = (Date.now() - joinedAt) / MS_IN_HOUR;

    if (timeDiffInHours < min) return leaderboard;

    leaderboard.forEach((entry) => {
      const increaseEvery = new RandomDataGenerator([entry.name, joinedAt]).realInRange(min, max);
      entry.level += Math.min(Math.floor(timeDiffInHours / increaseEvery), maxIncrease);
    });
    leaderboard.sort((a, b) => a.level - b.level);

    return leaderboard;
  }

  private opponentsToLeaderboardEntries(opponents: PlatformUser[], startLevel: number): LeaderboardEntry[] {
    let currentLevel = startLevel;

    return opponents.map<LeaderboardEntry>(({ name, avatarUrl, currentUser, id }) => {
      currentLevel += this.rnd.integerInRange(1, 3);
      return {
        id,
        name,
        avatarUrl,
        currentUser,
        level: currentLevel,
      };
    });
  }

  private getOpponentsForThePool(pool: number): PlatformUser[] {
    if (!this.realUsers || !this.fakeUsers) {
      throw new Error('LeaderboardPoolService - not initialized');
    }

    const { poolSize } = leaderboardConfig;

    const result: PlatformUser[] = [];

    if (this.realUsers.length > 0) {
      result.push(this.realUsers[pool % this.realUsers.length]);
    }

    let fakeUserIndex = pool * (poolSize - 1);

    while (result.length < poolSize - 1) {
      result.push(this.fakeUsers[fakeUserIndex % this.fakeUsers.length]);
      fakeUserIndex += 1;
    }

    this.rnd.shuffle(result);

    return result;
  }
}
